import { ReactNode, useCallback, useState } from 'react';
import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router';
import { Text } from 'react-aria-components';
import Button from 'src/components/ui/Button';
import PageTitle from 'src/components/creator/PageTitle';
import PageLoader from 'src/components/ui/PageLoader';
import ChatHistory from 'src/components/paul/ChatHistory';
import ChatBoxForm from 'src/components/paul/ChatBoxForm';
import TagButton from 'src/components/ui/TagButton';
import HatIcon from 'src/assets/icons/graduation-hat.svg';
import SchoolIcon from 'src/assets/icons/school-building.svg';

import useUser from 'src/hooks/useUser';
import usePaulSemanticSearch from 'src/hooks/usePaulSemanticSearch';
import { mapLadSelectionToMaterials } from 'src/components/creator/RequestOrderForm/materials-utils';

import type { ClientInformation } from 'src/types/client';
import type { IconProps } from 'src/types/common';
import type {
  LadLibrarySearchResult,
  PublicationScope,
} from 'src/types/lad-library';

const SCOPE_FILTERS: {
  scope: Exclude<PublicationScope, 'ALL'>;
  title: string;
  Icon: IconProps;
}[] = [
  { scope: 'CASE', title: 'Business Cases', Icon: SchoolIcon },
  { scope: 'OOP', title: 'Out-of-Print Library', Icon: HatIcon },
];

const PaulTestPage = () => {
  const { user, loading: userLoading } = useUser();
  const [restartCount, setRestartCount] = useState(0);

  if (userLoading) return <PageLoader />;
  if (!user.hasLadLibrarySearch) return <Navigate to="/" />;

  return (
    <>
      <PageTitle
        title="LAD Library"
        actionSlot={
          <Button
            onPress={() => setRestartCount((p) => p + 1)}
            variant="success"
          >
            Start Over
          </Button>
        }
      />
      <div className="bg-surface-alt rounded-xl shadow-3 mt-4 p-6 flex justify-center items-center flex-1 min-h-0">
        <Content key={restartCount} user={user} />
      </div>
    </>
  );
};

const Content = ({ user }: { user: ClientInformation }) => {
  const navigate = useNavigate();
  const { askPaul, loading, results } = usePaulSemanticSearch();
  // One filter at a time; none selected searches the whole library.
  const [scope, setScope] = useState<PublicationScope>('ALL');
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const hasMaterials = selectedMaterials.length > 0;

  const onMaterialToggle = useCallback(
    (material: { publicationId: string; isSelected: boolean }) => {
      setSelectedMaterials((prevState) => {
        if (material.isSelected) return [...prevState, material.publicationId];

        return prevState.filter((itemId) => itemId !== material.publicationId);
      });
    },
    []
  );

  // The order form starts from the selection: it rides along as history state,
  // which the create route reads to prefill its materials step.
  const onCreateOrder = () =>
    navigate({
      to: '/requests/create',
      state: {
        ladMaterials: mapLadSelectionToMaterials(
          results.flatMap(
            (result: { references: LadLibrarySearchResult[] }) =>
              result.references
          ),
          selectedMaterials
        ),
      },
    });

  if (results.length === 0) {
    return (
      <EmptyState
        username={user?.firstName ?? user?.lastName ?? user?.email ?? 'User'}
      >
        <ChatBoxForm
          onSubmit={(prompt) => askPaul(prompt, scope)}
          loading={loading}
        />
        <div className="flex gap-2 flex-wrap mt-4 px-4 mb-1.5">
          {SCOPE_FILTERS.map((filter) => (
            <TagButton
              key={filter.scope}
              variant="ai"
              Icon={filter.Icon}
              title={filter.title}
              isActive={scope === filter.scope}
              onPress={() =>
                setScope((current) =>
                  current === filter.scope ? 'ALL' : filter.scope
                )
              }
            />
          ))}
        </div>
      </EmptyState>
    );
  }

  return (
    <ChatHistory
      user={user}
      results={results}
      onMaterialToggle={onMaterialToggle}
      selectedMaterials={selectedMaterials}
    >
      <div className="flex items-start justify-center gap-4 px-7">
        <div
          className={`min-h-16 ${hasMaterials ? 'flex-1' : 'w-full max-w-[30rem]'}`}
        >
          <ChatBoxForm
            small
            onSubmit={(prompt) => askPaul(prompt, scope)}
            loading={loading}
          />
        </div>
        {hasMaterials && (
          <Button
            className="animate-in slide-in-from-right duration-200 ease-out"
            onPress={onCreateOrder}
            variant="success"
          >
            Create Order
          </Button>
        )}
      </div>
    </ChatHistory>
  );
};

const EmptyState = ({
  username,
  children,
}: {
  username: string;
  children: ReactNode;
}) => (
  <div className="text-center text-surface-alt-90 w-full max-w-[44rem] px-4 max-h-full overflow-auto">
    <Text elementType="strong" className="text-2xl font-bold">
      Hello, {username}
    </Text>
    <Text elementType="p" className="text-sm font-medium mt-2 mb-8">
      I'm here to help you find materials <br /> for your new Course Pack.
    </Text>

    {children}
  </div>
);

export const Route = createFileRoute('/_authenticated/_creator/lad-library')({
  component: PaulTestPage,
});
